import { EditOutlined } from '@ant-design/icons';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  type EEconomicOperatorValidationError,
  type ICountry,
  type IManufacturer,
  type IResponsiblePerson,
} from '@mahawi/eshop-common/src/types';
import { Button, Divider, Space, Tooltip } from 'antd';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { getColumnSearch } from '../../fragments/table-get-column-search';
import { type RootState } from '../../reducers';
import { SPACING } from '../../theme/tokens';
import DetailPageHeader from '../detail-page-header';
import Table from '../table';
import OperatorForm, {
  type IOperatorFormValues,
  toOperatorFormValues,
} from './operator-form';

interface IRow {
  key: string;
  operator: IManufacturer | IResponsiblePerson;
  name: string;
  address: string;
  country: string;
  contact: string;
  role: string;
}

const isResponsiblePerson = (
  operator: IManufacturer | IResponsiblePerson,
): operator is IResponsiblePerson => 'role' in operator;

function OperatorsPage({
  title,
  operators,
  withRole,
  inProcess,
  validationErrors,
  onReload,
  onSave,
}: {
  title: string;
  operators: (IManufacturer | IResponsiblePerson)[] | undefined;
  withRole: boolean;
  inProcess: boolean;
  validationErrors: EEconomicOperatorValidationError[];
  onReload: () => void;
  onSave: (values: IOperatorFormValues, countryType: ICountry) => void;
}): React.ReactElement {
  const [editing, setEditing] = useState<IOperatorFormValues | null>(null);
  const countries = useSelector((s: RootState) => s.Countries.countries);

  const dataSource: IRow[] = useMemo(
    (): IRow[] =>
      (operators ?? []).map(
        (operator: IManufacturer | IResponsiblePerson): IRow => ({
          key: operator.uuid,
          operator,
          name: operator.name,
          address: `${operator.street}, ${operator.zip} ${operator.city}`,
          country: operator.countryType.isoCode2,
          contact: [operator.email, operator.website]
            .filter(Boolean)
            .join(', '),
          role: isResponsiblePerson(operator) ? operator.role : '',
        }),
      ),
    [operators],
  );

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: IRow, b: IRow): number => stringCompare(a.name, b.name),
      ...getColumnSearch<IRow>('name'),
    },
    { title: 'Address', dataIndex: 'address', key: 'address' },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      sorter: (a: IRow, b: IRow): number => stringCompare(a.country, b.country),
    },
    { title: 'Contact', dataIndex: 'contact', key: 'contact' },
    ...(withRole ? [{ title: 'Role', dataIndex: 'role', key: 'role' }] : []),
    {
      title: '',
      key: 'actions',
      render: (_value: string, row: IRow): React.ReactElement => (
        <Tooltip title="Edit">
          <Button
            size="small"
            type="link"
            icon={<EditOutlined />}
            onClick={(): void => setEditing(toOperatorFormValues(row.operator))}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <DetailPageHeader
        title={title}
        onReload={onReload}
        isReloadDisabled={inProcess}
      />

      <OperatorForm
        initialValues={editing}
        countries={countries}
        withRole={withRole}
        inProcess={inProcess}
        validationErrors={validationErrors}
        onSubmit={onSave}
        onReset={(): void => setEditing(null)}
      />

      <Divider />

      <Table
        columns={columns as Parameters<typeof Table>[0]['columns']}
        dataSource={dataSource}
        isLoading={inProcess && !operators}
      />
    </Space>
  );
}

export default OperatorsPage;
